import { FC, useEffect, useState } from 'react';
import { Button, Form, Modal, Radio } from 'antd';

import close from '../../../assets/RoundedClose.svg';
import { DatePickerInput } from '../../Fields/DatePickerInput';
import { FloatSelect } from '../../Fields/FloatSelect';
import { useUpdateTaskMutation } from '../../../api/tenders';
import { tenderDataProps, TRFormDataI } from '../../../pages/TenderViewPage/types';
import { useGetSchoolTypesQuery } from '../../../api/common';
import { Loader } from '../../Loader/Loader';
import { useAppSelector } from '../../../hooks';
import Edit from '../../Icons/Edit';
import moment from 'moment';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    tenderData: tenderDataProps;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ContactDetailsModal: FC<ModalProps> = ({ openModal, setOpenModal, tenderData, setTrigger }) => {
    const [form] = Form.useForm();
    const { role } = useAppSelector((state: any) => state.profileReducer);

    const [updateTask, { isSuccess, isLoading }] = useUpdateTaskMutation();
    const types = {};
    const { data: dataSchoolTypes } = useGetSchoolTypesQuery(types);

    const [data, setData] = useState({
        contract_duration: '',
        service_start_date: '',
        contract_value: { name: '', label: '' }
    });

    const [SUData, setSUData] = useState({
        contract_duration: '',
        service_start_date: '',
        contract_value: { name: '', label: '' }
    });

    useEffect(() => {
        if (tenderData) {
            const taskData = tenderData?.phase_tasks?.find((elem) => elem.action === 'define_contract_details');

            setData({
                contract_duration: taskData?.contract_duration?.key ?? '',
                service_start_date: taskData?.service_start_date ?? '',
                contract_value: {
                    name: taskData?.contract_value?.value || '',
                    label: taskData?.contract_value?.key || ''
                }
            });
            setSUData({
                contract_duration: (taskData?.form_data as TRFormDataI)?.contract_duration?.su?.key ?? '',
                service_start_date: (taskData?.form_data as TRFormDataI)?.service_start_date?.su ?? '',
                contract_value: {
                    name: (taskData?.form_data as TRFormDataI)?.contract_value?.su?.value || '',
                    label: (taskData?.form_data as TRFormDataI)?.contract_value?.su?.key || ''
                }
            });
            form.setFieldsValue({
                radiogroup: taskData?.contract_duration?.key,
                service_start_date: taskData?.service_start_date,
                contract_value: taskData?.contract_value?.value
                    ? {
                          name: taskData?.contract_value?.value || '',
                          label: taskData?.contract_value?.key || ''
                      }
                    : ''
            });
        }
    }, [tenderData]);

    useEffect(() => {
        if (isSuccess) {
            setOpenModal(false);
            setTrigger((prev) => !prev);
        }
    }, [isSuccess]);

    const contactValue = dataSchoolTypes?.data?.estimated_contract_values?.find(
        (elem: any) => elem.key === data?.contract_value?.label
    )?.clear_value;

    const contactDuration = data?.contract_duration ? +data?.contract_duration : 0;

    const totalContactValue = contactValue * contactDuration;

    const handleFormSubmit = () => {
        form.validateFields()
            .then(() => {
                const dataToUpdate = {
                    ids: {
                        tender_id: tenderData?.id,
                        task_id: tenderData?.phase_tasks?.find((elem) => elem.action === 'define_contract_details')?.id
                    },

                    method: 'PUT',
                    data: {
                        contract_duration: dataSchoolTypes?.data?.contact_durations?.find(
                            (elem: any) => elem.key === data?.contract_duration
                        ),
                        contract_value: dataSchoolTypes?.data?.estimated_contract_values?.find(
                            (elem: any) => elem.key === data?.contract_value?.label
                        ),
                        total_contract_value: {
                            value: `R${totalContactValue}`,
                            clear_value: totalContactValue
                        },
                        service_start_date: moment(data?.service_start_date).format('YYYY-MM-DD')
                    }
                };
                if (isLoading) return;
                updateTask(dataToUpdate);
            })
            .catch((error) => {
                console.log('error', error);
            });
    };

    const isEditedDuration = SUData.contract_duration !== data?.contract_duration && role === 'tender_representative';
    const isEditedDate =
        moment(SUData.service_start_date).format('DD MM YYYY') !==
            moment(data?.service_start_date).format('DD MM YYYY') && role === 'tender_representative';
    const isEditedValue =
        SUData.contract_value?.label !== data?.contract_value?.label && role === 'tender_representative';

    return (
        <>
            <Modal
                className="modal-modal"
                destroyOnClose
                centered
                open={openModal}
                onOk={() => {}}
                onCancel={() => {
                    setOpenModal(false);
                }}
                closeIcon={<img src={close} />}
                footer={<></>}
            >
                <div className="modal">
                    <div className="modal__header meeting" style={{ height: '88px' }}>
                        <div className="modal__title">Commercial Overview</div>
                    </div>

                    <div className="modal__main main" style={{ padding: '24px 56px 43px 56px' }}>
                        <Form form={form} onFinish={handleFormSubmit}>
                            <div className="contact-details-modal">
                                <div className="main__title-no-head" style={{ margin: '0 0 6px 0' }}>
                                    Contract Duration
                                </div>
                                <div className={isEditedDuration ? 'edited' : ''}>
                                    <p className="main__title" style={{ margin: '0' }}>
                                        How long would you ideally like the agreement to run?
                                    </p>
                                    <p className="main__subtitle">
                                        You’re not locked into a fixed-term contract — you can still cancel if things
                                        aren’t working out.
                                    </p>
                                    <Form.Item
                                        className="input-wrapper"
                                        name="radiogroup"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'This field is required'
                                            }
                                        ]}
                                    >
                                        <Radio.Group name="radiogroup" className="main__radiogroup">
                                            {dataSchoolTypes?.data?.contact_durations?.map((elem: any) => {
                                                return (
                                                    <Radio
                                                        value={elem?.key}
                                                        onChange={() => {
                                                            setData((prev) => ({
                                                                ...prev,
                                                                contract_duration: elem?.key
                                                            }));
                                                        }}
                                                    >
                                                        {elem?.value}
                                                    </Radio>
                                                );
                                            })}
                                        </Radio.Group>
                                    </Form.Item>
                                    {isEditedDuration && (
                                        <div className="edited-text">
                                            <Edit fill="#E3A11F" />
                                            Edited by Tender Expert{' '}
                                            <div
                                                className="edited-text-btn"
                                                onClick={() => {
                                                    setData((prev) => ({
                                                        ...prev,
                                                        contract_duration: SUData?.contract_duration
                                                    }));
                                                    form.setFieldsValue({
                                                        radiogroup: SUData?.contract_duration
                                                    });
                                                }}
                                            >
                                                Undo
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="main__title-no-head" style={{ margin: '0 0 6px 0' }}>
                                    Service Start Date
                                </div>

                                <p className="main__title">When would you like the new service to begin?</p>
                                <div className={isEditedDate ? 'edited' : ''}>
                                    <Form.Item
                                        className="input-wrapper"
                                        name="service_start_date"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'This field is required'
                                            }
                                        ]}
                                    >
                                        <DatePickerInput
                                            isImage={true}
                                            onChange={(date: string) => {
                                                setData((prev) => ({
                                                    ...prev,
                                                    service_start_date: date
                                                }));
                                            }}
                                            value={data?.service_start_date}
                                            required={true}
                                            placeholder=""
                                            disabled={false}
                                            floatLabel="Service Start Date"
                                            filterDisabled={false}
                                        />
                                    </Form.Item>
                                    {isEditedDate && (
                                        <div className="edited-text">
                                            <Edit fill="#E3A11F" />
                                            Edited by Tender Expert{' '}
                                            <div
                                                className="edited-text-btn"
                                                onClick={() => {
                                                    setData((prev) => ({
                                                        ...prev,
                                                        service_start_date: SUData?.service_start_date
                                                    }));
                                                    form.setFieldsValue({
                                                        service_start_date: SUData?.service_start_date
                                                    });
                                                }}
                                            >
                                                Undo
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className={isEditedValue ? 'edited' : ''}>
                                    <Form.Item
                                        className="input-wrapper"
                                        style={{ margin: '10px 0 0 0' }}
                                        name="contract_value"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'This field is required'
                                            }
                                        ]}
                                    >
                                        <FloatSelect
                                            required={true}
                                            floatLabel={'Estimated Annual Contract Value *'}
                                            err={false}
                                            setSearch={() => {}}
                                            options={dataSchoolTypes?.data?.estimated_contract_values?.map(
                                                (elem: any) => ({
                                                    name: elem.value,
                                                    label: elem.key
                                                })
                                            )}
                                            onChange={(value: any) => {
                                                setData((prev) => ({
                                                    ...prev,
                                                    contract_value: value
                                                }));
                                            }}
                                            search={''}
                                            form={undefined}
                                            hasError={() => !!form.getFieldError(['contract_value']).length}
                                            type="common"
                                            value={
                                                data?.contract_value?.name
                                                    ? {
                                                          name: data?.contract_value?.name,
                                                          label: data?.contract_value?.label
                                                      }
                                                    : null
                                            }
                                        />
                                    </Form.Item>
                                    {isEditedValue && (
                                        <div className="edited-text">
                                            <Edit fill="#E3A11F" />
                                            Edited by Tender Expert{' '}
                                            <div
                                                className="edited-text-btn"
                                                onClick={() => {
                                                    setData((prev) => ({
                                                        ...prev,
                                                        contract_value: SUData?.contract_value
                                                    }));
                                                    form.setFieldsValue({
                                                        contract_value: SUData?.contract_value
                                                    });
                                                }}
                                            >
                                                Undo
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="main__subtitle" style={{ margin: '20px 0' }}>
                                    Please select what you currently spend — or expect to spend — per year for the full
                                    scope of the service, excluding VAT.
                                </p>
                                {!!totalContactValue && (
                                    <div className="main__note">
                                        <p className="subtitle">Total Estimated Contract Value</p>
                                        <p className="title">
                                            R{totalContactValue?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        </p>
                                        <p className="calc">
                                            calculated as: {contactValue?.clear_value} per year ×{' '}
                                            {data?.contract_duration} years
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyItems: 'space-between',
                                    gap: '16px',
                                    width: '100%'
                                }}
                            >
                                <Button block onClick={() => setOpenModal(false)}>
                                    Cancel
                                </Button>
                                <Button block type="primary" htmlType="submit">
                                    {isLoading ? <Loader /> : 'Save'}
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>
        </>
    );
};
